import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiError, mediaUrl } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import OrderTimeline from "@/components/OrderTimeline";
import PaymentBadge from "@/components/PaymentBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileDown, MessageCircle, Printer, RefreshCw, RotateCcw, Truck } from "lucide-react";
import { useReorder } from "@/lib/reorder";

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function OrderDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.state?.token || params.get("token") || "";

  const { reorder, busy: reordering } = useReorder(token);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [wa, setWa] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`, { params: token ? { token } : {} });
      setOrder(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then(({ data }) => setWa(data.whatsapp_number || "")).catch(() => {}); }, []);

  const retry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/retry-payment`, { phone_token: token });
      await openRazorpayCheckout({
        order: data,
        prefill: { name: data.user_name, email: data.user_email, contact: data.user_phone },
        onSuccess: async (resp) => {
          try {
            await api.post("/orders/verify-payment", {
              order_id: id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — invoice is on its way to your inbox");
            load();
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
        onFailure: (msg) => toast.error(msg),
        onDismiss: () => toast.info("Payment cancelled — you can retry any time"),
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const whatsappHelp = () => {
    const num = (wa || "").replace(/\D/g, "");
    if (!num) return toast.error("WhatsApp number not configured yet");
    const msg =
      `Namaste! I need help with my Immortal Vedas order.\n\n` +
      `• Order: #${order.id.slice(0, 8)}\n` +
      `• Amount: ${money(order.total)}\n` +
      `• Payment: ${order.payment_status}\n`;
    return window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-20 text-center" data-testid="order-detail-error">
        <h1 className="font-display text-3xl mb-3">We could not open this order</h1>
        <p className="text-muted-foreground text-sm mb-8">{error}</p>
        <Button variant="outline" className="btn-pill" onClick={() => navigate("/track")} data-testid="order-detail-track-link">
          Track with your phone number
        </Button>
      </main>
    );
  }
  if (!order) return <div className="p-12 text-center font-display text-2xl">Loading order…</div>;

  const paid = order.payment_status === "paid";
  const invoiceHref = `${API}/orders/${order.id}/invoice${token ? `?token=${encodeURIComponent(token)}` : ""}`;

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12" data-testid="order-detail-page">
      <button onClick={() => navigate(-1)} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5 mb-6" data-testid="order-detail-back">
        <ArrowLeft size={13} /> Back
      </button>

      <div className="overline mb-2">Order Details</div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display text-4xl lg:text-5xl mb-2" data-testid="order-detail-number">
            #{order.id.slice(0, 8)}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <PaymentBadge status={order.payment_status} needsReview={order.needs_review} testid="order-detail-payment-badge" />
            <Badge variant="outline" className="text-[10px] capitalize" data-testid="order-detail-status">
              {order.status?.replace(/_/g, " ")}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Placed {new Date(order.created_at).toLocaleString()}
              {order.invoice_number ? ` · ${order.invoice_number}` : ""}
            </span>
          </div>
        </div>
        <div className="font-display text-4xl" data-testid="order-detail-total">{money(order.total)}</div>
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6 items-start">
        <div className="space-y-6">
          <Card className="border-border">
            <CardContent className="p-6 lg:p-8">
              <h2 className="font-display text-2xl mb-5">Items</h2>
              <div className="space-y-4" data-testid="order-detail-items">
                {order.items?.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-4" data-testid={`order-item-${it.product_id}`}>
                    <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden shrink-0">
                      {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="text-xs text-muted-foreground">{money(it.price)} × {it.quantity}</div>
                    </div>
                    <div className="text-sm font-medium">{money(it.line_total)}</div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border mt-6 pt-5 space-y-2 text-sm" data-testid="order-detail-totals">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(order.gross_subtotal ?? order.subtotal)}</span></div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-primary" data-testid="order-detail-discount">
                    <span>Discount {order.discount_code ? `(${order.discount_code})` : ""}</span>
                    <span>− {money(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST @ {Math.round((order.tax_rate || 0) * 100)}%</span>
                  <span>{money(order.tax_amount)}</span>
                </div>
                <div className="flex justify-between font-display text-2xl pt-2">
                  <span>Total</span><span>{money(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-6 lg:p-8">
              <h2 className="font-display text-2xl mb-5">Journey</h2>
              <OrderTimeline status={order.status} history={order.status_history || []} />
              {order.failure_reason && (
                <p className="text-xs text-destructive mt-5" data-testid="order-detail-failure">Gateway said: {order.failure_reason}</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-border">
            <CardContent className="p-6">
              <h2 className="font-display text-xl mb-4">Delivery</h2>
              <div className="text-sm space-y-1" data-testid="order-detail-address">
                <div className="font-medium">{order.user_name}</div>
                <div className="text-muted-foreground whitespace-pre-line">{order.address}</div>
                <div className="text-muted-foreground">{order.user_phone}</div>
                <div className="text-muted-foreground">{order.user_email}</div>
              </div>
              {(order.awb_code || order.tracking_status) && (
                <div className="mt-5 pt-4 border-t border-border text-sm" data-testid="order-detail-courier">
                  <div className="flex items-center gap-2 font-medium mb-1"><Truck size={14} /> {order.courier_name || "Courier"}</div>
                  {order.awb_code && <div className="text-xs text-muted-foreground">AWB {order.awb_code}</div>}
                  {order.tracking_status && <div className="text-xs text-muted-foreground capitalize">{order.tracking_status}</div>}
                </div>
              )}
              {order.razorpay_payment_id && (
                <div className="mt-4 text-[11px] text-muted-foreground font-mono break-all" data-testid="order-detail-payment-id">
                  Payment {order.razorpay_payment_id}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-6 space-y-3">
              {paid ? (
                <Button variant="outline" className="btn-pill h-11 w-full border-primary/30" onClick={() => window.open(invoiceHref, "_blank")} data-testid="order-detail-invoice">
                  <FileDown size={14} className="mr-2" /> Download GST Invoice
                </Button>
              ) : (
                <Button className="btn-pill h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90" disabled={busy} onClick={retry} data-testid="order-detail-retry">
                  <RefreshCw size={14} className="mr-2" /> {busy ? "Opening…" : "Retry Payment"}
                </Button>
              )}
              <Button
                variant="outline"
                className="btn-pill h-11 w-full border-border"
                onClick={() => reorder(order.id)}
                disabled={Boolean(reordering)}
                data-testid="order-detail-reorder"
              >
                <RotateCcw size={14} className="mr-2" /> {reordering ? "Adding to cart…" : "Order this again"}
              </Button>
              <Button variant="outline" className="btn-pill h-11 w-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" onClick={whatsappHelp} data-testid="order-detail-whatsapp">
                <MessageCircle size={14} className="mr-2" /> Help on WhatsApp
              </Button>
              {order.is_admin_view && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <Button variant="outline" className="btn-pill h-10" onClick={() => window.open(`${API}/admin/shipping/orders/${order.id}/label?format=detailed`, "_blank")} data-testid="order-detail-label">
                    <Printer size={13} className="mr-1.5" /> Label
                  </Button>
                  <Button variant="outline" className="btn-pill h-10" onClick={() => window.open(`${API}/admin/shipping/orders/${order.id}/label?format=sticker`, "_blank")} data-testid="order-detail-sticker">
                    <Printer size={13} className="mr-1.5" /> Sticker
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}
